import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Stack,
  Backdrop,
} from "@mui/material";

interface LoadingSpinnerProps {
  /**
   * Whether to show the loading spinner
   */
  isLoading: boolean;

  /**
   * Optional loading message to display
   */
  message?: string;

  /**
   * Size of the spinner: 'small' | 'medium' | 'large'
   * @default 'medium'
   */
  size?: "small" | "medium" | "large";

  /**
   * If true, shows a full-screen backdrop loading state
   * @default false
   */
  fullScreen?: boolean;

  /**
   * If true, shows loading state over content
   * @default false
   */
  overlay?: boolean;

  /**
   * Custom color for the spinner
   */
  color?: "primary" | "secondary" | "success" | "error" | "warning" | "info";
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  isLoading,
  message,
  size = "medium",
  fullScreen = false,
  overlay = false,
  color = "primary",
}) => {
  // Don't render if not loading
  if (!isLoading) return null;

  // Determine spinner size
  const spinnerSize = {
    small: 32,
    medium: 48,
    large: 64,
  }[size];

  // Full screen loading state
  if (fullScreen) {
    return (
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: (theme) => theme.zIndex.modal + 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" size={spinnerSize} />
        {message && (
          <Typography variant="body1" sx={{ color: "#fff" }}>
            {message}
          </Typography>
        )}
      </Backdrop>
    );
  }

  // Overlay loading state
  if (overlay) {
    return (
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          backdropFilter: "blur(2px)",
          zIndex: 1000,
          borderRadius: "inherit",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={spinnerSize} color={color} />
          {message && (
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          )}
        </Stack>
      </Box>
    );
  }

  // Inline loading state
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
      <CircularProgress size={spinnerSize} color={color} />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Stack>
  );
};

export default LoadingSpinner;
